import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProductCapabilities } from "@/lib/useProductCapabilities";
import { isoToTr } from "@/lib/date";
import { Table, Btn } from "@/components/UI";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ExplicitFilterBar } from "@/components/common/ExplicitFilterBar";
import { LotFormModal } from "@/components/stock/LotFormModal";
import { LotAlerts, shelfLabel } from "@/components/stock/LotAlerts";
import { fmtQty, toQuery } from "@/lib/products";
import { apiError } from "@/lib/customers";

const INITIAL = { q: "", lot_no: "", exp_from: "", exp_to: "", over50: false };
const FIELDS = [
  { key: "q", label: "Ürün", width: "w-56" },
  { key: "lot_no", label: "Lot No", width: "w-36" },
  { key: "exp_from", label: "SKT Başlangıç", type: "date", width: "w-40" },
  { key: "exp_to", label: "SKT Bitiş", type: "date", width: "w-40" },
  { key: "over50", label: "%50 üzeri", type: "checkbox", width: "w-32" },
];

export function LotsTab() {
  const caps = useProductCapabilities();
  const [rows, setRows] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [filters, setFilters] = useState(INITIAL);
  const [modal, setModal] = useState({ open: false, lot: null });
  const load = (f = filters) => {
    const fail = (e) => setLoadErr(apiError(e, "Lotlar yüklenemedi"));
    api.get(`/stock/lots${toQuery(f)}`).then((r) => { setRows(r); setLoadErr(""); }).catch(fail);
    api.get("/stock/lots/alerts").then(setAlerts).catch(fail);
  };
  useEffect(() => { load(INITIAL); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (l) => {
    if (!window.confirm(`${l.lot_code} lotu silinsin mi? (Çöp Kutusu'ndan geri yüklenebilir)`)) return;
    try { await api.del(`/stock/lots/${l.id}`); load(); } catch (e) { alert(apiError(e, "Silme başarısız")); }
  };

  return (
    <div data-testid="lots-tab">
      <LotAlerts alerts={alerts} />
      {loadErr && <div className="mb-3 text-sm text-rose-600" data-testid="lots-load-error">{loadErr}</div>}
      {caps.lot_create && <div className="flex justify-end mb-2">
        <Btn onClick={() => setModal({ open: true, lot: null })} data-testid="new-lot-btn"><Plus size={14} /> Yeni Lot</Btn>
      </div>}
      <ExplicitFilterBar fields={FIELDS} initial={INITIAL} onApply={(f) => { setFilters(f); load(f); }} pdfPath="/stock/lots/pdf" pdfName="Lot_Listesi.pdf" testid="lot-filter" />
      <Table testid="lots-table" empty="Lot bulunamadı"
        columns={[
          { label: "Ürün", render: (l) => <div><div className="font-medium">{l.product_name}</div><div className="font-mono text-[11px] text-slate-500">{l.product_code}</div></div> },
          { label: "Lot No", render: (l) => <span className="font-mono" data-testid={`lot-row-no-${l.id}`}>{l.lot_code}</span> },
          { label: "Miktar", render: (l) => fmtQty(l.qty) },
          { label: "Üretim Tarihi", render: (l) => isoToTr(l.mfg_date) || "—" },
          { label: "SKT", render: (l) => isoToTr(l.expiry_date) || "—" },
          { label: "Raf Ömrü", render: (l) => (
            <span className={l.expired ? "text-rose-700 font-bold" : l.warn ? "text-amber-700 font-semibold" : ""} data-testid={`lot-row-pct-${l.id}`}>{shelfLabel(l)}</span>
          ) },
          { label: "Not", render: (l) => <span className="text-xs text-slate-600">{l.notes || ""}</span> },
          { label: "İşlem", render: (l) => (
            <div className="flex gap-1">
              {caps.lot_edit && <Btn size="sm" variant="secondary" onClick={() => setModal({ open: true, lot: l })} data-testid={`edit-lot-${l.id}`}><Pencil size={12} /></Btn>}
              {caps.lot_delete && <Btn size="sm" variant="danger" onClick={() => remove(l)} data-testid={`delete-lot-${l.id}`}><Trash2 size={12} /></Btn>}
            </div>
          ) },
        ]}
        rows={rows} />
      <LotFormModal open={modal.open} lot={modal.lot} onClose={() => setModal({ open: false, lot: null })}
        onSaved={() => { setModal({ open: false, lot: null }); load(); }} />
    </div>
  );
}
